import { DefaultVoyageType } from '@hooks/LaunchDarkly/LaunchDarkly.types';
import { DEFAULT_VALUE, PERK_TYPES } from 'components/Promo/constants';
import { ANY, TRACKING_ACTION, TRACKING_INTERACTION } from 'contexts/AnalyticsContext/Constants';
import { HOME_PAGE_QUERY_PARAM } from 'contexts/CruiseSearchContext/constants';
import format from 'date-fns/format';
import { enUS } from 'date-fns/locale';
import { fill } from 'lib/helpers';
import { SailingSrc, StateroomClassPriceSrc } from 'types';
import { STATEROOM_CLASS_TO_DEAL } from './constants';
import { PricingDeal } from './types';

export const getNumberGuest = (adults, children) => {
	if (adults && children) {
		return Number(adults[0]) + Number(children[0]);
	}
	if (adults) {
		return adults[0];
	}
	if (children) {
		return children[0];
	}
	return ANY;
};

export const getInteraction = (searchIndex: number, resultsLength: number, masterSailing: SailingSrc, action) => {
	return `cruise search: ${searchIndex} of ${resultsLength}: ${masterSailing?.itinerary?.totalNights} night ${masterSailing?.itinerary?.name}: ${action}`;
};

export const getItineraryCardInfo = (id: string, masterSailing: SailingSrc, lowestPriceSailing: SailingSrc) => {
	return `IC:${id}:${lowestPriceSailing?.id}:${lowestPriceSailing?.startDate}:${masterSailing?.itinerary?.destination.code}:${lowestPriceSailing?.lowestStateroomClassPrice?.price?.value}`;
};

export const getSearchMonths = (search) => {
	let startDate = '',
		endDate = '';
	const REGEXP_PARAM_VALUE_TYPE_DATE = '[12]\\d{3}\\/(0[1-9]|1[0-2])\\/(0[1-9]|[12]\\d|3[01])';
	const regExp = new RegExp(REGEXP_PARAM_VALUE_TYPE_DATE);
	let dates = null;

	if (search?.startDate && Array.isArray(search?.startDate)) {
		const start = search.startDate[0];
		const end = search.startDate[search.startDate.length - 1];
		if (start?.includes('~') && end?.includes('~')) {
			const [firstStartDate] = start?.replace(/-/g, '/')?.split('~') || [];
			const [, lastEndDate] = end?.replace(/-/g, '/')?.split('~') || [];

			dates = [firstStartDate, lastEndDate];
		}
	}
	const isValid = dates && Array.isArray(dates) && (dates || []).every((date) => regExp.test(date));

	if (isValid) {
		startDate = getFormatcruiseSearchMonths(dates[0]);
		endDate = getFormatcruiseSearchMonths(dates[1]);
		if (startDate === endDate) {
			endDate = '';
		}
	}

	const endSearchMonth = endDate ? `-${endDate}` : '';
	let searchMonths = DEFAULT_VALUE;
	if (startDate) {
		searchMonths = `${startDate}${endSearchMonth}`;
	}

	return searchMonths;
};

export const getFormatFill = (times, field) => {
	return fill(Array(times), field);
};

export const getFormatcruiseSearchMonths = (date) => {
	return `${new Date(date).getFullYear()} ${format(new Date(date), 'MMM', {
		locale: enUS,
	})}`;
};

export const getFormatPromoDashboard = (perks) => {
	const promotionPerks = [];
	if (perks) {
		perks.slice(0, 3).forEach((perk) => {
			const perkInfo = `Hero:${perk.name}`;

			if (perk.__typename === PERK_TYPES.boolean) {
				promotionPerks.push(`${perkInfo}:0`);
			}
			if (perk.__typename === PERK_TYPES.percentOff) {
				promotionPerks.push(`${perkInfo}${perk.target}:${perk.percent}`);
			}
			if (perk.__typename === PERK_TYPES.price) {
				promotionPerks.push(`${perkInfo}:${perk.price.value}`);
			}
		});
	}

	return promotionPerks;
};

export const getTotalDays = (nights) => {
	let totalDays = ANY;
	if (nights.length) {
		const maxNights = nights[nights.length - 1];
		if (maxNights.indexOf('~') >= 0) {
			totalDays = maxNights.split('~')[1];
		} else {
			totalDays = maxNights.replaceAll(/\D/g, '');
		}
	}
	return totalDays;
};

export function getCruiseSearchType(search, query = {}) {
	if (query[HOME_PAGE_QUERY_PARAM]) {
		return `${TRACKING_INTERACTION.SEARCH_ORIGIN.SEARCH_WIDGET_INIT}${query[HOME_PAGE_QUERY_PARAM]}`;
	}
	if (Object.keys(search).length) {
		return TRACKING_INTERACTION.SEARCH_ORIGIN.FILTER_UPDATE;
	}
	if (query['q']) {
		return TRACKING_INTERACTION.SEARCH_ORIGIN.NL_SEARCH;
	}
	return ANY;
}

export function getCruiseType(search, trackingAction) {
	if (search?.voyageType) {
		return search.voyageType.join(',').toLowerCase();
	}
	if (Object.keys(search).length === 0) {
		return trackingAction === TRACKING_ACTION.SEARCH_UPDATE ? ANY : DefaultVoyageType.OCEAN.toLowerCase();
	}
	return ANY;
}

export function reduceDeparturePorts(departurePorts, riverDeparturePorts) {
	if (!departurePorts?.length) return ANY;
	const combinedDeparturePorts = departurePorts
		.filter((port) => !riverDeparturePorts.includes(port))
		.concat(riverDeparturePorts.map((port) => `r-${port}`));
	return combinedDeparturePorts;
}

export const getUrgencyParam = (urgencyText) => {
	if (!urgencyText) return 'no urgency text';
	if (urgencyText.includes('view')) return 'people viewing';
	if (urgencyText.includes('watch')) return 'people watching';
	if (urgencyText.includes('book')) return 'people booking';
};

export const getImpressionPricingPanel = (stateroomClassPricing: StateroomClassPriceSrc[]) => {
	const reversedStaterooms = stateroomClassPricing?.slice().reverse();
	const deal = reversedStaterooms.reduce((foundDeal, currentClass, currentIndex, staterooms) => {
		if (foundDeal === PricingDeal.None) {
			const remainingStaterooms = staterooms.slice(currentIndex + 1);
			const dealExists = Boolean(remainingStaterooms.find((item) => currentClass?.price?.value <= item?.price?.value));

			if (dealExists) {
				return STATEROOM_CLASS_TO_DEAL[currentClass?.stateroomClass.id];
			}
		}

		return foundDeal;
	}, PricingDeal.None);

	return deal;
};
